import React from 'react';
import { Link } from 'react-router-dom';
import FieldPrint, {fieldCount} from "./FieldPrint";

interface Step3Props {
  s: {[key: string]: string[]};
  setS: React.Dispatch<React.SetStateAction<{[key: string]: string[]}>>;
  inp: {[key: string]: string};
}

function Step3(props: Step3Props) {
  const { s, inp } = props;

  const nobodyLabel = 'niemand';
  const noLabel = 'no';

  const rowIsNobodyLabel = (row: string) => {
    return s[row]
      .filter((f) => f !== '-')
      .every((e) => e === nobodyLabel);
  };

  const rowsIntersect = (rows1: string[], rows2: string[]) => {
    rows1 = rows1.flatMap((r) => s[r])
      .filter((f) => f !== '-')
      .filter((f) => f !== nobodyLabel);
    rows2 = rows2.flatMap((r) => s[r])
      .filter((f) => f !== '-')
      .filter((f) => f !== nobodyLabel);

    return rows1.filter(value => rows2.includes(value)).length > 0;
  };

  const rowsAreSame = (r1: string, r2: string) => {
    const row1 = s[r1]
      .filter((f) => f !== '-')
      .filter((value, index, self) => {
        return self.indexOf(value) === index;
      })
      .sort();
    const row2 = s[r2]
      .filter((f) => f !== '-')
      .filter((value, index, self) => {
        return self.indexOf(value) === index;
      })
      .sort();
    return JSON.stringify(row1) === JSON.stringify(row2);
  };

  return (
    <div className="step step3">
      <h2>Schritt 3: Auflösung – identifizierte Probleme</h2>

      <p>
        Auf Basis Ihrer Antworten wurden folgende <strong>potenzielle Probleme</strong> identifiziert. Diese
        Problemliste ist als <strong>Anregung</strong> zu verstehen, genauer darüber nachzudenken, ob diese
        Probleme im <strong>konkreten Fall</strong> wirklich bestehen und
        welche <strong>Auswirkungen</strong> sie haben können, und erhebt insbesondere <strong>keinen
        Anspruch auf Vollständigkeit</strong>.
      </p>

      <h3>Problemkreis 1</h3>
      <p><em>Lücken in der Aufgabenverteilung
      </em></p>
      <ul className="results">
        { rowIsNobodyLabel('4') &&
          <li id="results-38">Es ist möglichst bald zu klären, wer über die <strong>Einführung des Algorithmensystems
          </strong>
            {' '}
            entscheiden kann.
          </li>
        }
        { rowIsNobodyLabel('5') &&
        <li id="results-39">Es ist möglichst bald zu klären, wer das <strong>Algorithmensystem entwickelt</strong>.</li>
        }
        { rowIsNobodyLabel('6') &&
          <li id="results-40">Es ist möglichst bald zu klären, wer das <strong>Algorithmensystem implementiert</strong>.</li>
        }
        { rowIsNobodyLabel('7') &&
          <li id="results-41">Es ist möglichst bald zu klären, wer das <strong>Algorithmensystem anwendet</strong>.</li>
        }
        { rowIsNobodyLabel('8') &&
        <li id="results-42">Es ist möglichst bald zu klären, wer für die <strong>Sicherheit des Algorithmensystems</strong>
          {' '}
          zuständig ist.
        </li>
        }
        { rowIsNobodyLabel('9') &&
        <li id="results-43">Es ist möglichst bald zu klären, wer die <strong>Datenbasis verwaltet</strong>, mit der das
          System arbeitet.
        </li>
        }
        { rowIsNobodyLabel('10') &&
          <li id="results-44">Es ist möglichst bald zu klären, wer das <strong>System evaluiert</strong>.</li>
        }
        { !rowIsNobodyLabel('10') && rowsIntersect(['4'], ['10']) &&
        <li id="results-45-1">Da die gleiche Person oder Gruppe für die Evaluierung wie für <span>die Entscheidung, ob das Algorithmensystem eingeführt wird,</span> zuständig ist, kann eine <strong>unabhängige Evaluierung nicht gewährleistet</strong>
          {' '}
          werden.
        </li>
        }
        <li id="no-results-1">Wir konnten keine offensichtlichen Probleme dieser Art identifizieren.</li>
      </ul>

      <h3>Problemkreis 2</h3>
      <p><em>Fehlende Unabhängigkeit der Evaluierung
      </em></p>
      <ul className="results">
        { !rowIsNobodyLabel('10') && rowsIntersect(['5'], ['10']) &&
        <li id="results-45-1">Da die gleiche Person oder Gruppe für die Evaluierung wie für <span>die Entwicklung des Algorithmensystems</span> zuständig ist, kann eine <strong>unabhängige Evaluierung nicht gewährleistet</strong>
          {' '}
          werden.
        </li>
        }
        { !rowIsNobodyLabel('10') && rowsIntersect(['6'], ['10']) &&
        <li id="results-45-1">Da die gleiche Person oder Gruppe für die Evaluierung wie für <span>die Implementierung des Algorithmensystems</span> zuständig ist, kann eine <strong>unabhängige Evaluierung nicht gewährleistet</strong>
          {' '}
          werden.
        </li>
        }
        { !rowIsNobodyLabel('10') && rowsIntersect(['7'], ['10']) &&
        <li id="results-45-1">Da die gleiche Person oder Gruppe für die Evaluierung wie für <span>die Anwendung des Algorithmensystems</span> zuständig ist, kann eine <strong>unabhängige Evaluierung nicht gewährleistet</strong>
          {' '}
          werden.
        </li>
        }
        { !rowIsNobodyLabel('10') && rowsIntersect(['8'], ['10']) &&
        <li id="results-45-1">Da die gleiche Person oder Gruppe für die Evaluierung wie für <span>die Sicherheit des Algorithmensystems</span> zuständig ist, kann eine <strong>unabhängige Evaluierung nicht gewährleistet</strong>
          {' '}
          werden.
        </li>
        }
        { !rowIsNobodyLabel('10') && rowsIntersect(['9'], ['10']) &&
        <li id="results-45-1">Da die gleiche Person oder Gruppe für die Evaluierung wie für <span>die Datenverwaltung</span> zuständig ist, kann eine <strong>unabhängige Evaluierung nicht gewährleistet</strong>
          {' '}
          werden.
        </li>
        }
        <li id="no-results-1">Wir konnten keine offensichtlichen Probleme dieser Art identifizieren.</li>
      </ul>

      <h3>Problemkreis 3</h3>
      <p><em>Jemand ist für Aufgaben verantwortlich, die nicht ihr:ihm zugeteilt sind</em></p>
      <ul className="results">
        { !rowIsNobodyLabel('12') && !rowIsNobodyLabel('5') && !rowIsNobodyLabel('4') &&
        !rowsIntersect(['12'], ['5', '4']) &&
        <li id="results-47-1"><FieldPrint s={s} row='12' /> {fieldCount(s, '12') > 1 ? 'sind' : 'ist'} verantwortlich für die Erfüllung der Zielvorgabe des Systems, aber <FieldPrint s={s} row='5' /> und <FieldPrint s={s} row='4' /> haben es als Aufgabe; das stellt ein potenzielles
          Problem dar, sofern <FieldPrint s={s} row='12' /> nicht die Arbeit von <FieldPrint s={s} row='5' /> und <FieldPrint s={s} row='4' /> {fieldCount(s, '12') > 1 ? 'kontrollieren' : 'kontrolliert'}, gegenüber <FieldPrint s={s} row='5' /> und <FieldPrint s={s} row='4' /> weisungsbefugt {fieldCount(s, '12') > 1 ? 'sind' : 'ist'} oder in
          einem ähnlichen Rollenverhältnis {fieldCount(s, '12') > 1 ? 'stehen' : 'steht'}. Ohne <strong>Einfluss auf den Aufgabenbereich</strong> kann auch keine <strong>Verantwortung dafür</strong> übernommen werden.
        </li>
        }
        { !rowIsNobodyLabel('13') && !rowIsNobodyLabel('6') &&
        !rowsIntersect(['13'], ['6']) &&
        <li id="results-47-2"><FieldPrint s={s} row='13' /> {fieldCount(s, '13') > 1 ? 'sind' : 'ist'} verantwortlich für die richtige Implementierung des Systems, aber <FieldPrint s={s} row='6' /> {fieldCount(s, '6') > 1 ? 'haben' : 'hat'} es als Aufgabe; das stellt ein potenzielles
          Problem dar, sofern <FieldPrint s={s} row='13' /> nicht die Arbeit von <FieldPrint s={s} row='6' /> {fieldCount(s, '13') > 1 ? 'kontrollieren' : 'kontrolliert'}, gegenüber <FieldPrint s={s} row='6' /> weisungsbefugt {fieldCount(s, '13') > 1 ? 'sind' : 'ist'} oder in
          einem ähnlichen Rollenverhältnis {fieldCount(s, '13') > 1 ? 'stehen' : 'steht'}. Ohne <strong>Einfluss auf den Aufgabenbereich</strong> kann auch keine <strong>Verantwortung dafür</strong> übernommen werden.
        </li>
        }
        { !rowIsNobodyLabel('14') && !rowIsNobodyLabel('9') &&
        !rowsIntersect(['14'], ['7']) &&
        <li id="results-47-3"><FieldPrint s={s} row='14' /> {fieldCount(s, '14') > 1 ? 'sind' : 'ist'} verantwortlich für datenschutzrechtliche Beschwerden, aber <FieldPrint s={s} row='7' /> {fieldCount(s, '7') > 1 ? 'haben' : 'hat'} es als Aufgabe; das stellt ein potenzielles
          Problem dar, sofern <FieldPrint s={s} row='14' /> nicht die Arbeit von <FieldPrint s={s} row='7' /> {fieldCount(s, '14') > 1 ? 'kontrollieren' : 'kontrolliert'}, gegenüber <FieldPrint s={s} row='7' /> weisungsbefugt {fieldCount(s, '14') > 1 ? 'sind' : 'ist'} oder in
          einem ähnlichen Rollenverhältnis {fieldCount(s, '14') > 1 ? 'stehen' : 'steht'}. Ohne <strong>Einfluss auf den Aufgabenbereich</strong> kann auch keine <strong>Verantwortung dafür</strong> übernommen werden.
        </li>
        }
        { !rowIsNobodyLabel('15') && !rowIsNobodyLabel('8') &&
        !rowsIntersect(['15'], ['8']) &&
        <li id="results-47-4"><FieldPrint s={s} row='15' /> {fieldCount(s, '15') > 1 ? 'sind' : 'ist'} verantwortlich für die Integrität des Systems, aber <FieldPrint s={s} row='8' /> {fieldCount(s, '8') > 1 ? 'haben' : 'hat'} es als Aufgabe; das stellt ein potenzielles
          Problem dar, sofern <FieldPrint s={s} row='15' /> nicht die Arbeit von <FieldPrint s={s} row='8' /> {fieldCount(s, '15') > 1 ? 'kontrollieren' : 'kontrolliert'}, gegenüber <FieldPrint s={s} row='8' /> weisungsbefugt {fieldCount(s, '15') > 1 ? 'sind' : 'ist'} oder in
          einem ähnlichen Rollenverhältnis {fieldCount(s, '15') > 1 ? 'stehen' : 'steht'}. Ohne <strong>Einfluss auf den Aufgabenbereich</strong> kann auch keine <strong>Verantwortung dafür</strong> übernommen werden.
        </li>
        }
        { !rowIsNobodyLabel('16') && !rowIsNobodyLabel('7') &&
        !rowsIntersect(['16'], ['9']) &&
        <li id="results-47-5"><FieldPrint s={s} row='16' /> {fieldCount(s, '16') > 1 ? 'sind' : 'ist'} verantwortlich für die korrekte Anwendung des Systems, aber <FieldPrint s={s} row='9' /> {fieldCount(s, '9') > 1 ? 'haben' : 'hat'} es als Aufgabe; das stellt ein potenzielles
          Problem dar, sofern <FieldPrint s={s} row='16' /> nicht die Arbeit von <FieldPrint s={s} row='9' /> {fieldCount(s, '16') > 1 ? 'kontrollieren' : 'kontrolliert'}, gegenüber <FieldPrint s={s} row='9' /> weisungsbefugt {fieldCount(s, '16') > 1 ? 'sind' : 'ist'} oder in
          einem ähnlichen Rollenverhältnis {fieldCount(s, '16') > 1 ? 'stehen' : 'steht'}. Ohne <strong>Einfluss auf den Aufgabenbereich</strong> kann auch keine <strong>Verantwortung dafür</strong> übernommen werden.
        </li>
        }
        <li id="no-results-2">Wir konnten keine offensichtlichen Probleme dieser Art identifizieren.</li>
      </ul>

      <h3>Problemkreis 4</h3>
      <p><em>Lücken in der Verantwortung</em></p>
      <ul className="results">
        { rowIsNobodyLabel('12') &&
        <li id="results-49">Da für die Probleme mit der <strong>Erfüllung der Zielvorgaben</strong> niemand zuständig ist,
          besteht hier potenziell eine Verantwortungslücke.
        </li>
        }
        { rowIsNobodyLabel('13') &&
        <li id="results-50">Da für Probleme mit der <strong>korrekten Implementierung</strong> niemand zuständig ist, besteht
          hier potenziell eine Verantwortungslücke.
        </li>
        }
        { rowIsNobodyLabel('14') &&
        <li id="results-51">Da für Probleme mit <strong>datenschutzrechtlichen Beschwerden</strong> niemand zuständig ist,
          besteht hier potenziell eine Verantwortungslücke.
        </li>
        }
        { rowIsNobodyLabel('15') &&
        <li id="results-52">Da für Probleme mit der <strong>Verletzung der Systemintegrität</strong> niemand zuständig ist,
          besteht hier potenziell eine Verantwortungslücke.
        </li>
        }
        { rowIsNobodyLabel('16') &&
        <li id="results-53">Da für Probleme aus der <strong>falschen Anwendung</strong> des Systems niemand zuständig ist,
          besteht hier potenziell eine Verantwortungslücke.
        </li>
        }
        { !rowIsNobodyLabel('12') && fieldCount(s, '12') > 1 &&
        <li id="results-54-1">Potenziell kommt es zu einer Überschneidung von Verantwortung, weil für die
          {' '}
          <strong>Erfüllung der Zielvorgaben</strong> mit <FieldPrint s={s} row='12' /> mehrere verantwortlich sind.
        </li>
        }
        { !rowIsNobodyLabel('13') && fieldCount(s, '13') > 1 &&
        <li id="results-54-2">Potenziell kommt es zu einer Überschneidung von Verantwortung, weil für die
          {' '}
          <strong>korrekte Implementierung</strong> des Algorithmensystems mit <FieldPrint s={s} row='13' /> mehrere  verantwortlich sind.
        </li>
        }
        { !rowIsNobodyLabel('14') && fieldCount(s, '14') > 1 &&
        <li id="results-54-3">Potenziell kommt es zu einer Überschneidung von Verantwortung, weil für
          {' '}
          <strong>datenschutzrechtliche Probleme</strong> mit <FieldPrint s={s} row='14' /> mehrere verantwortlich sind.
        </li>
        }
        { !rowIsNobodyLabel('15') && fieldCount(s, '15') > 1 &&
        <li id="results-54-4">Potenziell kommt es zu einer Überschneidung von Verantwortung, weil für die
          {' '}
          <strong>Sicherheit und Systemintegrität</strong> des Algorithmensystems mit <FieldPrint s={s} row='15' /> mehrere  verantwortlich sind.
        </li>
        }
        { !rowIsNobodyLabel('16') && fieldCount(s, '16') > 1 &&
        <li id="results-54-5">Potenziell kommt es zu einer Überschneidung von Verantwortung, weil für die
          {' '}
          <strong>richtige Anwendung</strong> des Algorithmensystems mit <FieldPrint s={s} row='16' /> mehrere  verantwortlich sind.
        </li>
        }
        <li id="no-results-3">Wir konnten keine offensichtlichen Probleme dieser Art identifizieren.</li>
      </ul>

      <h3>Problemkreis 5</h3>
      <p><em>Verantwortlichen Personen fehlt die Ermächtigung, um ein Problem zu lösen</em></p>
      <ul className="results">
        { !rowIsNobodyLabel('12') && !rowsIntersect(['12'], ['18', '19']) &&
        <li id="results-56">Die {fieldCount(s, '12') > 1 ? 'Personen' : 'Person'}, die für die <strong>Erfüllung der Zielvorgaben</strong> des Systems verantwortlich
          {' '}{fieldCount(s, '12') > 1 ? 'sind' : 'ist'} (<FieldPrint s={s} row='12' />), {fieldCount(s, '12') > 1 ? 'können' : 'kann'} mangels Ermächtigung die Verantwortung dafür in der Praxis nicht übernehmen.
          </li>
        }
        { !rowIsNobodyLabel('13') && !rowsIntersect(['13'], ['19']) &&
        <li id="results-57">Die {fieldCount(s, '13') > 1 ? 'Personen' : 'Person'}, die für die <strong>korrekte Implementierung</strong> verantwortlich {fieldCount(s, '13') > 1 ? 'sind' : 'ist'} (<FieldPrint s={s} row='13' />), {fieldCount(s, '13') > 1 ? 'können' : 'kann'}
          {' '}mangels Ermächtigung die Verantwortung dafür in der Praxis nicht übernehmen.
        </li>
        }
        { !rowIsNobodyLabel('14') && !rowsIntersect(['14'], ['20']) &&
        <li id="results-58">Die {fieldCount(s, '14') > 1 ? 'Personen' : 'Person'}, die für <strong>datenschutzrechtliche Probleme</strong> verantwortlich {fieldCount(s, '14') > 1 ? 'sind' : 'ist'} (<FieldPrint s={s} row='14' />), {fieldCount(s, '14') > 1 ? 'können' : 'kann'}
          {' '}mangels Ermächtigung die Verantwortung dafür in der Praxis nicht übernehmen.
        </li>
        }
        { !rowIsNobodyLabel('15') && !rowsIntersect(['15'], ['21']) &&
        <li id="results-59">Die {fieldCount(s, '15') > 1 ? 'Personen' : 'Person'}, die für die <strong>Systemsicherheit</strong> zuständig {fieldCount(s, '15') > 1 ? 'sind' : 'ist'} (<FieldPrint s={s} row='15' />), {fieldCount(s, '15') > 1 ? 'können' : 'kann'} mangels
          {' '}Ermächtigung die Verantwortung dafür in der Praxis nicht übernehmen.
        </li>
        }
        { !rowIsNobodyLabel('16') && !rowsIntersect(['16'], ['19']) &&
        <li id="results-60">Die {fieldCount(s, '16') > 1 ? 'Personen' : 'Person'}, die für die <strong>korrekte Anwendung</strong> des Systems verantwortlich {fieldCount(s, '16') > 1 ? 'sind' : 'ist'} (<FieldPrint s={s} row='16' />), {fieldCount(s, '16') > 1 ? 'können' : 'kann'}
          {' '}mangels Ermächtigung die Verantwortung dafür in der Praxis nicht übernehmen.
        </li>
        }
        { rowIsNobodyLabel('18') &&
        <li id="results-61">Da niemand die Ermächtigung hat, das Algorithmensystem <strong>wieder abzuschalten</strong>,
          können grundsätzliche Probleme, die sich aus seinem Einsatz ergeben, nicht behoben werden.
        </li>
        }
        { rowIsNobodyLabel('19') &&
        <li id="results-62">Da niemand die Ermächtigung hat, die <strong>Implementierung bzw. praktische Anwendung
          des Systems zu ändern</strong>, können praktische Probleme, die sich aus seinem Einsatz ergeben, nicht
          behoben werden.
        </li>
        }
        { rowIsNobodyLabel('20') &&
        <li id="results-63">Da niemand die Ermächtigung hat, <strong>Daten zu korrigieren bzw. zu löschen</strong>, können
          Probleme mit dem Datenbestand (inkl. Datenschutzprobleme) nicht behoben werden.
        </li>
        }
        { rowIsNobodyLabel('21') &&
        <li id="results-64">Da niemand die Ermächtigung hat, <strong>Sicherheitsvorkehrungen einzurichten</strong>, können
          Probleme mit der IT-Sicherheit des Systems nicht behoben werden.
        </li>
        }
        <li id="no-results-4">Wir konnten keine offensichtlichen Probleme dieser Art identifizieren.</li>
      </ul>


      <h3>Problemkreis 6</h3>
      <p><em>Fehlende Kommunikations-/Beschwerde-/Feedbackwege</em></p>
      <ul className="results">
        { !(rowIsNobodyLabel('7') || rowIsNobodyLabel('9')) && !rowsAreSame('7', '9') && inp['23'] === noLabel &&
        <li id="results-66">Da <strong>zwischen Anwender:innen und für die Daten Zuständigen</strong> kein
          Kommunikations-/Beschwerde-/Feedbackweg besteht, können <strong>Probleme mit der Datenbasis</strong> nicht
          (zeitnah) aufgezeigt und/oder behoben werden.
        </li>
        }
        { !(rowIsNobodyLabel('7') || rowIsNobodyLabel('5')) && !rowsAreSame('7', '5') && inp['24'] === noLabel &&
        <li id="results-67">Da <strong>zwischen Anwender:innen und Entwickler:innen</strong> kein
          Kommunikations-/Beschwerde-/Feedbackweg besteht, können <strong>Probleme im laufenden Betrieb</strong> nicht
          (zeitnah) aufgezeigt und das System nicht entsprechend angepasst werden.
        </li>
        }
        { !(rowIsNobodyLabel('7') || rowIsNobodyLabel('6')) && !rowsAreSame('7', '6') && inp['25'] === noLabel &&
        <li id="results-68">Da <strong>zwischen Anwender:innen und Implementierer:innen</strong> kein
          Kommunikations-/Beschwerde-/Feedbackweg besteht, können <strong>Probleme in der organisatorischen Nutzung</strong>,
          aus <strong>mangelnder Schulung</strong> oder <strong>ähnlichen praktischen Aspekten</strong> resultierende Probleme nicht (zeitnah)
          aufgezeigt und/oder behoben werden.
        </li>
        }
        { !(rowIsNobodyLabel('5') || rowIsNobodyLabel('6')) && !rowsAreSame('5', '6') && inp['26'] === noLabel &&
        <li id="results-69">Da <strong>zwischen Entwickler:innen und Implementierer:innen</strong> kein
          Kommunikations-/Beschwerde-/Feedbackweg besteht, besteht die Gefahr, dass die technische
          Entwicklung und die praktische Implementierung in den Organisationsabläufen <strong>nicht ausreichend
          aufeinander abgestimmt</strong> sind.
        </li>
        }
        { !rowIsNobodyLabel('4') && inp['27'] === noLabel &&
        <li id="results-70">Da keine ausreichenden Kommunikations-/Beschwerde-/Feedbackwege zu den Personen
          bestehen, die <strong>über die Benutzung des Systems prinzipiell entscheiden</strong> können, besteht die Gefahr,
          dass das System <strong>trotz grundlegender Fehlfunktion, schwerer technischer Probleme oder ähnlichen
          Schwierigkeiten</strong> über zu lange Zeit in Betrieb bleibt.
        </li>
        }
        <li id="no-results-5">Wir konnten keine offensichtlichen Probleme dieser Art identifizieren.</li>
      </ul>

      <hr />
      <p>
        Wir freuen uns über Feedback, Erfahrungsberichte und dergleichen unter <a href="mailto:vera@l17r.eu">vera@l17r.eu</a>.
      </p>

      <div className="stepNav">
        <Link className="btn btn-secondary" to="/step2d">Zurück</Link>
        <span/>
      </div>

    </div>
  );
}

export default Step3
;